import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  FileDownload as FileDownloadIcon,
  Close as CloseIcon,
  MergeType as MergeIcon,
} from '@mui/icons-material';
import { requestService } from '../../services/requestService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { addLogoToPDF } from '../../utils/pdfLogoHelper';

interface AreaStats {
  area: string;
  services: number;
  amount: number;
}

interface ServiceProviderStats {
  providerId: number;
  providerName: string;
  area: string;
  phone: string;
  services: number;
  amount: number;
  requests?: any[];
}

interface MergedProviderStats {
  providerName: string;
  areas: string;
  phones: string;
  services: number;
  amount: number;
  requests: any[];
}

type ReportType = 'area' | 'provider' | 'providerExcludeArea';
type DateField = 'createdAt' | 'assignedAt' | 'completedDate';

const ServiceProviderReport: React.FC = () => {
  const [reportType, setReportType] = useState<ReportType>('area');
  const [dateField, setDateField] = useState<DateField>('createdAt');
  const [areaStats, setAreaStats] = useState<AreaStats[]>([]);
  const [providerStats, setProviderStats] = useState<ServiceProviderStats[]>([]);
  const [mergedProviderStats, setMergedProviderStats] = useState<MergedProviderStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [usingCache, setUsingCache] = useState(false);
  const [cachedRequests, setCachedRequests] = useState<any[]>([]);
  const [cachedPeriod, setCachedPeriod] = useState<{ start: string; end: string; dateField: DateField } | null>(null);
  
  // Modal state for showing request details
  const [modalOpen, setModalOpen] = useState(false);
  const [modalRequests, setModalRequests] = useState<any[]>([]);
  const [modalTitle, setModalTitle] = useState<string>('');

  // Use refs to always have current values in callbacks
  const cachedRequestsRef = useRef<any[]>([]);
  const cachedPeriodRef = useRef<{ start: string; end: string; dateField: DateField } | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    cachedRequestsRef.current = cachedRequests;
  }, [cachedRequests]);

  useEffect(() => {
    cachedPeriodRef.current = cachedPeriod;
  }, [cachedPeriod]);

  // Period (Current month by default)
  const [periodStart, setPeriodStart] = useState<string>(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [periodEnd, setPeriodEnd] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Define processing functions first so they can be referenced
  const loadAreaReport = useCallback((requests: any[]) => {
    // Use requestIdleCallback or setTimeout to avoid blocking UI
    const processData = () => {
      console.log('Processing area report with', requests.length, 'requests');
      
      // Group by area
      const areaMap = new Map<string, { services: number; amount: number }>();
      requests.forEach((request: any) => {
        const attrs = request.attributes || request;
        const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
        const area = provider?.area || 'Unknown';
        const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
        const amount = invoice?.total ? parseFloat(invoice.total) : 0;

        if (!areaMap.has(area)) {
          areaMap.set(area, { services: 0, amount: 0 });
        }
        const stats = areaMap.get(area)!;
        stats.services += 1;
        stats.amount += amount;
      });

      // Convert to array and sort by services (descending)
      const stats: AreaStats[] = Array.from(areaMap.entries())
        .map(([area, data]) => ({
          area,
          services: data.services,
          amount: data.amount,
        }))
        .filter((stat) => stat.services > 0)
        .sort((a, b) => b.services - a.services);

      console.log('Area report processed:', stats.length, 'areas, total services:', 
        stats.reduce((sum, s) => sum + s.services, 0));
      setAreaStats(stats);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(processData, { timeout: 100 });
    } else {
      setTimeout(processData, 0);
    }
  }, []);

  const loadProviderReport = useCallback((requests: any[]) => {
    // Use requestIdleCallback or setTimeout to avoid blocking UI
    const processData = () => {
      console.log('Processing provider report with', requests.length, 'requests');
      
      // Analyze dates by the selected field
      const dateMap = new Map<string, number>();
      const providerDateMap = new Map<string, Map<string, number>>();
      
      requests.forEach((request: any) => {
        const attrs = request.attributes || request;
        const dateValue = attrs.createdAt || request.createdAt;
        const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
        const providerName = provider?.name || 'Unknown';
        
        if (dateValue) {
          const dateOnly = dateValue.split('T')[0];
          dateMap.set(dateOnly, (dateMap.get(dateOnly) || 0) + 1);
          
          // Track by provider
          if (!providerDateMap.has(providerName)) {
            providerDateMap.set(providerName, new Map());
          }
          const providerDates = providerDateMap.get(providerName)!;
          providerDates.set(dateOnly, (providerDates.get(dateOnly) || 0) + 1);
        }
      });
      
      console.log('All requests by creation date:', Object.fromEntries(dateMap));
      console.log('Requests grouped by provider and date:');
      providerDateMap.forEach((dates, provider) => {
        console.log(`  ${provider}:`, Object.fromEntries(dates));
      });
      
      // Group by service provider
      const providerMap = new Map<number, { 
        name: string; 
        area: string; 
        phone: string; 
        services: number; 
        amount: number;
        requests: any[];
      }>();

      let skippedCount = 0;
      const skippedRequests: any[] = [];
      
      requests.forEach((request: any, index: number) => {
        const attrs = request.attributes || request;
        const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
        const providerId = attrs.serviceProvider?.data?.id || attrs.serviceProvider?.id;
        
        if (!providerId) {
          skippedCount++;
          skippedRequests.push({
            index,
            id: request.id,
            requestNumber: attrs.requestNumber,
            reason: 'No provider ID'
          });
          return;
        }

        const providerName = provider?.name || 'Unknown';
        const area = provider?.area || 'Unknown';
        const phone = provider?.phone || '-';
        const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
        const amount = invoice?.total ? parseFloat(invoice.total) : 0;

        if (!providerMap.has(providerId)) {
          providerMap.set(providerId, {
            name: providerName,
            area,
            phone,
            services: 0,
            amount: 0,
            requests: [],
          });
        }
        const stats = providerMap.get(providerId)!;
        stats.services += 1;
        stats.amount += amount;
        stats.requests.push(request);
      });

      if (skippedCount > 0) {
        console.log('⚠️ Skipped', skippedCount, 'requests without service provider:', skippedRequests);
      }

      // Convert to array and sort by services (descending)
      const stats: ServiceProviderStats[] = Array.from(providerMap.entries())
        .map(([providerId, data]) => ({
          providerId,
          providerName: data.name,
          area: data.area,
          phone: data.phone,
          services: data.services,
          amount: data.amount,
          requests: data.requests,
        }))
        .filter((stat) => stat.services > 0)
        .sort((a, b) => b.services - a.services);

      console.log('✅ Provider report processed:', stats.length, 'providers, total services:', 
        stats.reduce((sum, s) => sum + s.services, 0));
      console.log('All providers in report:', stats.map(s => `${s.providerName} (${s.services})`).join(', '));
      
      // Log details for ALL providers to see what's happening
      console.log('\n📊 Detailed provider breakdown:');
      providerMap.forEach((data, providerId) => {
        const dateBreakdown = new Map<string, number>();
        data.requests.forEach((req: any) => {
          const attrs = req.attributes || req;
          const createdAt = attrs.createdAt || req.createdAt;
          if (createdAt) {
            const dateOnly = createdAt.split('T')[0];
            dateBreakdown.set(dateOnly, (dateBreakdown.get(dateOnly) || 0) + 1);
          }
        });
        console.log(`  Provider ID ${providerId}: "${data.name}" - ${data.services} services, ${data.area}, ${data.phone}`);
        console.log(`    Dates:`, Object.fromEntries(dateBreakdown));
      });
      
      setProviderStats(stats);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(processData, { timeout: 100 });
    } else {
      setTimeout(processData, 0);
    }
  }, []);

  const loadMergedProviderReport = useCallback((requests: any[]) => {
    const processData = () => {
      console.log('Processing merged provider report with', requests.length, 'requests');
      
      // Normalize provider name for comparison (case-insensitive, trim whitespace)
      const normalizeProviderName = (name: string) => {
        return name.trim().toLowerCase();
      };
      
      // Group by normalized provider name
      const mergedMap = new Map<string, { 
        originalName: string;
        areas: Set<string>;
        phones: Set<string>;
        services: number; 
        amount: number;
        requests: any[];
      }>();

      let skippedCount = 0;
      
      requests.forEach((request: any) => {
        const attrs = request.attributes || request;
        const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
        const providerId = attrs.serviceProvider?.data?.id || attrs.serviceProvider?.id;
        
        if (!providerId) {
          skippedCount++;
          return;
        }

        const providerName = provider?.name || 'Unknown';
        const normalizedName = normalizeProviderName(providerName);
        const area = provider?.area || 'Unknown';
        const phone = provider?.phone || '-';
        const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
        const amount = invoice?.total ? parseFloat(invoice.total) : 0;

        if (!mergedMap.has(normalizedName)) {
          mergedMap.set(normalizedName, {
            originalName: providerName, // Use first occurrence's name
            areas: new Set<string>(),
            phones: new Set<string>(),
            services: 0,
            amount: 0,
            requests: [],
          });
        }
        const stats = mergedMap.get(normalizedName)!;
        stats.areas.add(area);
        stats.phones.add(phone);
        stats.services += 1;
        stats.amount += amount;
        stats.requests.push(request);
      });

      if (skippedCount > 0) {
        console.log('⚠️ Skipped', skippedCount, 'requests without service provider');
      }

      // Convert to array and sort by services (descending)
      const stats: MergedProviderStats[] = Array.from(mergedMap.entries())
        .map(([normalizedName, data]) => ({
          providerName: data.originalName,
          areas: Array.from(data.areas).sort().join(', '),
          phones: Array.from(data.phones).filter(p => p !== '-').sort().join(', ') || '-',
          services: data.services,
          amount: data.amount,
          requests: data.requests,
        }))
        .filter((stat) => stat.services > 0)
        .sort((a, b) => b.services - a.services);

      console.log('✅ Merged provider report processed:', stats.length, 'unique providers, total services:', 
        stats.reduce((sum, s) => sum + s.services, 0));
      
      setMergedProviderStats(stats);
    };

    // Use requestIdleCallback if available, otherwise setTimeout
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(processData, { timeout: 100 });
    } else {
      setTimeout(processData, 0);
    }
  }, []);

  // Auto-load report on mount
  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount - loadReport will use initial state values

  // Process cached data when tab changes (no need to refetch)
  useEffect(() => {
    if (cachedRequests.length > 0 && cachedPeriod) {
      if (reportType === 'area') {
        loadAreaReport(cachedRequests);
      } else if (reportType === 'provider') {
        loadProviderReport(cachedRequests);
      } else if (reportType === 'providerExcludeArea') {
        loadMergedProviderReport(cachedRequests);
      }
    }
  }, [reportType, cachedRequests, cachedPeriod, loadAreaReport, loadProviderReport, loadMergedProviderReport]);

  const loadReport = useCallback(async () => {
    console.log('loadReport called with period:', periodStart, 'to', periodEnd, 'dateField:', dateField);
    console.log('Current cache (ref):', cachedPeriodRef.current, 'Cached requests count:', cachedRequestsRef.current.length);
    
    // Check if we have cached data for this period - use refs for current values
    const currentCachedPeriod = cachedPeriodRef.current;
    const currentCachedRequests = cachedRequestsRef.current;
    
    if (
      currentCachedPeriod &&
      currentCachedPeriod.start === periodStart &&
      currentCachedPeriod.end === periodEnd &&
      currentCachedPeriod.dateField === dateField &&
      currentCachedRequests.length > 0
    ) {
      console.log('✅ Using cached data - no API call needed');
      setUsingCache(true);
      // Use cached data
      if (reportType === 'area') {
        loadAreaReport(currentCachedRequests);
      } else if (reportType === 'provider') {
        loadProviderReport(currentCachedRequests);
      } else if (reportType === 'providerExcludeArea') {
        loadMergedProviderReport(currentCachedRequests);
      }
      return;
    }

    console.log('🔄 Fetching NEW data for period:', periodStart, 'to', periodEnd, 'by', dateField);
    setUsingCache(false);
    
    try {
      setLoading(true);
      setError('');
      setLoadingProgress(0);

      // Get all requests for the selected period - fetch all pages
      let allRequests: any[] = [];

      // Fetch first page with larger page size to reduce API calls
      // Filter by selected date field
      const firstPageResponse = await requestService.getAll({
        'populate[serviceProvider]': '*',
        'populate[invoice]': '*',
        'filters[serviceProvider][id][$notNull]': true,
        [`filters[${dateField}][$gte]`]: `${periodStart}T00:00:00.000Z`,
        [`filters[${dateField}][$lte]`]: `${periodEnd}T23:59:59.999Z`,
        'pagination[page]': 1,
        'pagination[pageSize]': 1000, // Increased from 100 to reduce API calls
      });
      
      // Extract data correctly - handle multiple response formats
      // Format can be: { data: [...], meta: {...} } or { data: { data: [...], meta: {...} } }
      const firstPageData = firstPageResponse?.data?.data || firstPageResponse?.data || 
        (Array.isArray(firstPageResponse) ? firstPageResponse : []);
      const pagination = firstPageResponse?.data?.meta?.pagination || 
        firstPageResponse?.meta?.pagination || {};
      allRequests = Array.isArray(firstPageData) ? [...firstPageData] : [];
      
      console.log('Service Provider Report - First page loaded:', {
        requestsCount: allRequests.length,
        total: pagination.total,
        pageCount: pagination.pageCount,
        responseFormat: firstPageResponse?.data?.data ? 'nested' : 'flat',
        dateField: dateField,
        periodStart: periodStart,
        periodEnd: periodEnd
      });
      
      // Log sample request to verify data structure
      if (allRequests.length > 0) {
        const sample = allRequests[0];
        const attrs = sample.attributes || sample;
        console.log('Sample request structure:', {
          id: sample.id,
          requestNumber: attrs.requestNumber,
          createdAt: attrs.createdAt,
          assignedAt: attrs.assignedAt,
          completedDate: attrs.completedDate,
          providerId: attrs.serviceProvider?.data?.id || attrs.serviceProvider?.id,
          providerName: attrs.serviceProvider?.data?.attributes?.name || attrs.serviceProvider?.name
        });
      }
      
      // Update progress after first page
      const totalPages = pagination.pageCount || 1;
      if (totalPages > 0) {
        setLoadingProgress(Math.round((1 / totalPages) * 100));
      }
      
      // Fetch remaining pages in parallel batches
      if (totalPages > 1) {
        const batchSize = 10; // Increased batch size for better performance
        const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
        
        for (let i = 0; i < remainingPages.length; i += batchSize) {
          const batch = remainingPages.slice(i, i + batchSize);
          const batchPromises = batch.map(pageNum =>
            requestService.getAll({
              'populate[serviceProvider]': '*',
              'populate[invoice]': '*',
              'filters[serviceProvider][id][$notNull]': true,
              [`filters[${dateField}][$gte]`]: `${periodStart}T00:00:00.000Z`,
              [`filters[${dateField}][$lte]`]: `${periodEnd}T23:59:59.999Z`,
              'pagination[page]': pageNum,
              'pagination[pageSize]': 1000, // Increased from 100
            })
          );
          
          const batchResults = await Promise.all(batchPromises);
          for (const response of batchResults) {
            // Extract data correctly - handle multiple response formats
            const pageData = response?.data?.data || response?.data || 
              (Array.isArray(response) ? response : []);
            if (Array.isArray(pageData)) {
              allRequests = [...allRequests, ...pageData];
            }
          }
          
          // Update progress
          const completedPages = Math.min(i + batchSize + 1, totalPages);
          const progress = Math.round((completedPages / totalPages) * 100);
          setLoadingProgress(progress);
          
          console.log(`Service Provider Report - Loaded batch ${Math.floor(i / batchSize) + 1}, total requests: ${allRequests.length}, progress: ${progress}%`);
        }
      }

      // Ensure progress reaches 100%
      setLoadingProgress(100);
      console.log('Service Provider Report - All pages loaded, total requests:', allRequests.length);

      // Cache the data
      const newPeriod = { start: periodStart, end: periodEnd, dateField };
      setCachedRequests(allRequests);
      setCachedPeriod(newPeriod);
      cachedRequestsRef.current = allRequests;
      cachedPeriodRef.current = newPeriod;

      // Process the data
      if (reportType === 'area') {
        loadAreaReport(allRequests);
      } else if (reportType === 'provider') {
        loadProviderReport(allRequests);
      } else if (reportType === 'providerExcludeArea') {
        loadMergedProviderReport(allRequests);
      }
    } catch (err: any) {
      console.error('Error loading report:', err);
      const errorMessage = err.response?.data?.error?.message 
        || err.message 
        || 'Failed to load report. Please try again.';
      setError(errorMessage);
      // Clear cached data on error
      setCachedRequests([]);
      setCachedPeriod(null);
      cachedRequestsRef.current = [];
      cachedPeriodRef.current = null;
      setLoadingProgress(0);
    } finally {
      setLoading(false);
      setLoadingProgress(0);
    }
  }, [periodStart, periodEnd, dateField, reportType, loadAreaReport, loadProviderReport, loadMergedProviderReport]);
  // Note: Using refs for cached data to avoid stale closure issues

  const formatCurrency = useCallback((amount: number) => {
    return `OMR ${amount.toFixed(3)}`;
  }, []);

  const formatDate = useCallback((dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }, []);

  const getPeriodLabel = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
      const month = startDate.toLocaleDateString('en-US', { month: 'long' });
      return month.charAt(0).toUpperCase() + month.slice(1);
    }
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    console.log('Downloading PDF - Report type:', reportType);
    console.log('Area stats count:', areaStats.length);
    console.log('Provider stats count:', providerStats.length);
    console.log('Cached requests count:', cachedRequests.length);
    
    const doc = new jsPDF();
    
    // Load and add logo
    await addLogoToPDF(doc);
    
    // Company header
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('DARB NATIONAL SERVICE LLC', 20, 38);
    doc.setFontSize(20);
    doc.text('DARB National Services Co LLC', 105, 20, { align: 'center' });
    
    doc.setFontSize(12);
    doc.text('P.O Box 417, PC 100, Muscat, Sultanate of Oman', 105, 28, { align: 'center' });
    doc.text('Phone: +968 24790888 | Email: info@darbns.com', 105, 34, { align: 'center' });
    
    // Title
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    const reportTitle = reportType === 'area' 
      ? 'Service Report by Area' 
      : reportType === 'provider'
      ? 'Service Report by Service Provider'
      : 'Service Report by Service Provider (Merged)';
    doc.text(reportTitle, 105, 45, { align: 'center' });
    
    // Period
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Period: ${formatDate(periodStart)} - ${formatDate(periodEnd)}`,
      105,
      52,
      { align: 'center' }
    );

    let tableData: string[][];
    let headers: string[];

    if (reportType === 'area') {
      tableData = areaStats.map((stat) => [
        stat.area,
        stat.services.toString(),
        formatCurrency(stat.amount),
      ]);
      headers = ['Area', 'Services', 'Amount'];
    } else if (reportType === 'provider') {
      tableData = providerStats.map((stat) => [
        stat.providerName,
        stat.area,
        stat.phone,
        stat.services.toString(),
        formatCurrency(stat.amount),
      ]);
      headers = ['Service Provider', 'Area', 'Phone', 'Services', 'Amount'];
    } else {
      tableData = mergedProviderStats.map((stat) => [
        stat.providerName,
        stat.areas,
        stat.phones,
        stat.services.toString(),
        formatCurrency(stat.amount),
      ]);
      headers = ['Service Provider', 'Areas', 'Phone', 'Services', 'Amount'];
    }

    autoTable(doc, {
      startY: 60,
      head: [headers],
      body: tableData,
      theme: 'grid',
      headStyles: {
        fillColor: [0, 0, 0],
        textColor: 255,
        fontStyle: 'bold',
      },
      styles: {
        fontSize: 9,
      },
    });

    // Totals
    const finalY = (doc as any).lastAutoTable.finalY || 60;
    const totalServices = reportType === 'area'
      ? areaStats.reduce((sum, stat) => sum + stat.services, 0)
      : reportType === 'provider'
      ? providerStats.reduce((sum, stat) => sum + stat.services, 0)
      : mergedProviderStats.reduce((sum, stat) => sum + stat.services, 0);
    const totalAmount = reportType === 'area'
      ? areaStats.reduce((sum, stat) => sum + stat.amount, 0)
      : reportType === 'provider'
      ? providerStats.reduce((sum, stat) => sum + stat.amount, 0)
      : mergedProviderStats.reduce((sum, stat) => sum + stat.amount, 0);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Total Services: ${totalServices}`, 20, finalY + 15);
    doc.text(`Total Amount: ${formatCurrency(totalAmount)}`, 20, finalY + 22);

    const fileName = reportType === 'area'
      ? `Service-Area-Report-${getPeriodLabel(periodStart, periodEnd)}.pdf`
      : reportType === 'provider'
      ? `Service-Provider-Report-${getPeriodLabel(periodStart, periodEnd)}.pdf`
      : `Service-Provider-Merged-Report-${getPeriodLabel(periodStart, periodEnd)}.pdf`;
    doc.save(fileName);
  };

  const handleDownloadCSV = () => {
    console.log('Downloading CSV - Report type:', reportType);
    console.log('Area stats count:', areaStats.length);
    console.log('Provider stats count:', providerStats.length);
    console.log('Merged provider stats count:', mergedProviderStats.length);
    console.log('Cached requests count:', cachedRequests.length);
    
    if ((reportType === 'area' && areaStats.length === 0) ||
        (reportType === 'provider' && providerStats.length === 0) ||
        (reportType === 'providerExcludeArea' && mergedProviderStats.length === 0)) {
      console.log('No stats to download');
      return;
    }

    let headers: string[];
    let rows: string[][];

    if (reportType === 'area') {
      headers = ['Area', 'Services', 'Amount'];
      rows = areaStats.map((stat) => [
        stat.area,
        stat.services.toString(),
        formatCurrency(stat.amount),
      ]);
    } else if (reportType === 'provider') {
      headers = ['Service Provider', 'Area', 'Phone', 'Services', 'Amount'];
      rows = providerStats.map((stat) => [
        stat.providerName,
        stat.area,
        stat.phone,
        stat.services.toString(),
        formatCurrency(stat.amount),
      ]);
    } else {
      headers = ['Service Provider', 'Areas', 'Phone', 'Services', 'Amount'];
      rows = mergedProviderStats.map((stat) => [
        stat.providerName,
        stat.areas,
        stat.phones,
        stat.services.toString(),
        formatCurrency(stat.amount),
      ]);
    }

    // Add totals row
    const totalServices = reportType === 'area'
      ? areaStats.reduce((sum, stat) => sum + stat.services, 0)
      : reportType === 'provider'
      ? providerStats.reduce((sum, stat) => sum + stat.services, 0)
      : mergedProviderStats.reduce((sum, stat) => sum + stat.services, 0);
    const totalAmount = reportType === 'area'
      ? areaStats.reduce((sum, stat) => sum + stat.amount, 0)
      : reportType === 'provider'
      ? providerStats.reduce((sum, stat) => sum + stat.amount, 0)
      : mergedProviderStats.reduce((sum, stat) => sum + stat.amount, 0);
    
    rows.push([]); // Empty row
    if (reportType === 'area') {
      rows.push(['Total', totalServices.toString(), formatCurrency(totalAmount)]);
    } else {
      rows.push(['Total', '', '', totalServices.toString(), formatCurrency(totalAmount)]);
    }

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    const fileName = reportType === 'area'
      ? 'Service-Area'
      : reportType === 'provider'
      ? 'Service-Provider'
      : 'Service-Provider-Merged';
    link.setAttribute(
      'download',
      `${fileName}-Report-${getPeriodLabel(periodStart, periodEnd)}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Memoize totals to avoid recalculating on every render
  const totalServices = useMemo(() => {
    if (reportType === 'area') {
      return areaStats.reduce((sum, stat) => sum + stat.services, 0);
    } else if (reportType === 'provider') {
      return providerStats.reduce((sum, stat) => sum + stat.services, 0);
    } else {
      return mergedProviderStats.reduce((sum, stat) => sum + stat.services, 0);
    }
  }, [reportType, areaStats, providerStats, mergedProviderStats]);

  const totalAmount = useMemo(() => {
    if (reportType === 'area') {
      return areaStats.reduce((sum, stat) => sum + stat.amount, 0);
    } else if (reportType === 'provider') {
      return providerStats.reduce((sum, stat) => sum + stat.amount, 0);
    } else {
      return mergedProviderStats.reduce((sum, stat) => sum + stat.amount, 0);
    }
  }, [reportType, areaStats, providerStats, mergedProviderStats]);

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: ReportType) => {
    setReportType(newValue);
  }, []);

  // Clear cache when period changes
  const handlePeriodStartChange = useCallback((value: string) => {
    console.log('Period start changed to:', value, 'clearing cache');
    setPeriodStart(value);
    setCachedRequests([]);
    setCachedPeriod(null);
    cachedRequestsRef.current = [];
    cachedPeriodRef.current = null;
  }, []);

  const handlePeriodEndChange = useCallback((value: string) => {
    console.log('Period end changed to:', value, 'clearing cache');
    setPeriodEnd(value);
    setCachedRequests([]);
    setCachedPeriod(null);
    cachedRequestsRef.current = [];
    cachedPeriodRef.current = null;
  }, []);

  const handleServicesClick = useCallback((requests: any[], title: string) => {
    setModalRequests(requests);
    setModalTitle(title);
    setModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalRequests([]);
    setModalTitle('');
  }, []);

  const handleDownloadModalCSV = useCallback(() => {
    if (modalRequests.length === 0) {
      console.log('No requests to download');
      return;
    }

    const headers = ['Request Number', 'Service Provider', 'Area', 'Phone', 'Created Date', 'Assigned Date', 'Completed Date', 'Amount'];
    const rows = modalRequests.map((request) => {
      const attrs = request.attributes || request;
      const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
      const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
      const amount = invoice?.total ? parseFloat(invoice.total) : 0;

      return [
        attrs.requestNumber || '-',
        provider?.name || 'Unknown',
        provider?.area || 'Unknown',
        provider?.phone || '-',
        attrs.createdAt ? formatDate(attrs.createdAt) : '-',
        attrs.assignedAt ? formatDate(attrs.assignedAt) : '-',
        attrs.completedDate ? formatDate(attrs.completedDate) : '-',
        formatCurrency(amount),
      ];
    });

    // Convert to CSV format
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute(
      'download',
      `Request-Details-${modalTitle.replace(/[^a-z0-9]/gi, '-')}-${new Date().toISOString().split('T')[0]}.csv`
    );
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [modalRequests, modalTitle, formatDate, formatCurrency]);

  return (
    <Box>
      <style>
        {`
          @media print {
            .no-print {
              display: none !important;
            }
            @page {
              margin: 1cm;
            }
          }
        `}
      </style>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3} className="no-print">
        <Typography variant="h4">Service Provider Reports</Typography>
        <Stack direction="row" spacing={2}>
          <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={handleDownloadCSV}>
            Download CSV
          </Button>
          <Button variant="outlined" startIcon={<PdfIcon />} onClick={handleDownloadPDF}>
            Download PDF
          </Button>
          <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>
            Print
          </Button>
        </Stack>
      </Stack>

      {/* Report Type Tabs */}
      <Paper sx={{ mb: 3 }} className="no-print">
        <Tabs 
          value={reportType} 
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            icon={<LocationIcon />} 
            iconPosition="start"
            label="By Area" 
            value="area"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          />
          <Tab 
            icon={<PersonIcon />} 
            iconPosition="start"
            label="By Service Provider" 
            value="provider"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          />
          <Tab 
            icon={<MergeIcon />} 
            iconPosition="start"
            label="Service Provider (exclude area)" 
            value="providerExcludeArea"
            sx={{ textTransform: 'none', fontWeight: 'medium' }}
          />
        </Tabs>
      </Paper>

      {/* Period Filters */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" gutterBottom>
          Select Period
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Filter By Date</InputLabel>
            <Select
              value={dateField}
              label="Filter By Date"
              onChange={(e) => {
                setDateField(e.target.value as DateField);
                setCachedRequests([]);
                setCachedPeriod(null);
                cachedRequestsRef.current = [];
                cachedPeriodRef.current = null;
              }}
            >
              <MenuItem value="createdAt">Created Date</MenuItem>
              <MenuItem value="assignedAt">Assigned Date</MenuItem>
              <MenuItem value="completedDate">Completed Date</MenuItem>
            </Select>
          </FormControl>
          <Stack direction="row" spacing={1}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              value={periodStart}
              onChange={(e) => handlePeriodStartChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              size="small"
              value={periodEnd}
              onChange={(e) => handlePeriodEndChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={loadReport}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {usingCache && !loading && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setUsingCache(false)}>
          Using cached data. Click "Generate Report" to refresh with latest data.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ minHeight: '400px', p: 3 }}>
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="300px">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Loading report data...
            </Typography>
            <Box sx={{ width: '100%', maxWidth: 400, mt: 2 }}>
              <LinearProgress variant="determinate" value={loadingProgress} />
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {loadingProgress}%
              </Typography>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Stack direction="row" spacing={2} mb={3} className="no-print">
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Services
                </Typography>
                <Typography variant="h4">{totalServices}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {getPeriodLabel(periodStart, periodEnd)}
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ flex: 1 }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Amount
                </Typography>
                <Typography variant="h4">{formatCurrency(totalAmount)}</Typography>
                <Typography variant="caption" color="textSecondary">
                  {getPeriodLabel(periodStart, periodEnd)}
                </Typography>
              </CardContent>
            </Card>
          </Stack>

          {/* Report Table */}
          <Paper>
            <Box sx={{ p: 3 }}>
              <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 'bold', mb: 1 }}>
                {reportType === 'area' 
                  ? 'Service Report by Area' 
                  : reportType === 'provider' 
                  ? 'Service Report by Service Provider' 
                  : 'Service Report by Service Provider (Merged)'}
              </Typography>
              <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
                Period: {getPeriodLabel(periodStart, periodEnd)}
              </Typography>

              {reportType === 'area' ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          Services
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {areaStats.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              No data found for the selected period.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        areaStats.map((stat) => {
                          // Get requests for this area
                          const areaRequests = cachedRequests.filter((request: any) => {
                            const attrs = request.attributes || request;
                            const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
                            const area = provider?.area || 'Unknown';
                            return area === stat.area;
                          });

                          return (
                            <TableRow key={stat.area}>
                              <TableCell>
                                <Typography variant="body2" fontWeight="medium">
                                  {stat.area}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="text"
                                  color="primary"
                                  onClick={() => handleServicesClick(areaRequests, `${stat.area} - Services`)}
                                  sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                                >
                                  {stat.services}
                                </Button>
                              </TableCell>
                              <TableCell align="right">{formatCurrency(stat.amount)}</TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : reportType === 'provider' ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Service Provider</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          Services
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {providerStats.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              No data found for the selected period.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        providerStats.map((stat) => (
                          <TableRow key={stat.providerId}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {stat.providerName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {stat.area}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {stat.phone}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => handleServicesClick(stat.requests || [], `${stat.providerName} - Services`)}
                                sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                              >
                                {stat.services}
                              </Button>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(stat.amount)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableCell sx={{ fontWeight: 'bold' }}>Service Provider</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Areas</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          Services
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {mergedProviderStats.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                            <Typography color="textSecondary">
                              No data found for the selected period.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        mergedProviderStats.map((stat, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {stat.providerName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {stat.areas}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="textSecondary">
                                {stat.phones}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Button
                                variant="text"
                                color="primary"
                                onClick={() => handleServicesClick(stat.requests, `${stat.providerName} - Services`)}
                                sx={{ textTransform: 'none', minWidth: 'auto', p: 0.5 }}
                              >
                                {stat.services}
                              </Button>
                            </TableCell>
                            <TableCell align="right">{formatCurrency(stat.amount)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Totals */}
              {((reportType === 'area' && areaStats.length > 0) || 
                (reportType === 'provider' && providerStats.length > 0) ||
                (reportType === 'providerExcludeArea' && mergedProviderStats.length > 0)) && (
                <Box sx={{ mt: 3, p: 2, backgroundColor: '#f9f9f9', borderRadius: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight="bold">
                      Total Services: {totalServices}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                      Total Amount: {formatCurrency(totalAmount)}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Box>
          </Paper>
        </>
      )}

      {/* Request Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{modalTitle}</Typography>
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold' }}>Request #</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Service Provider</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Area</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Assigned</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Completed</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        No requests found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  modalRequests.map((request) => {
                    const attrs = request.attributes || request;
                    const provider = attrs.serviceProvider?.data?.attributes || attrs.serviceProvider;
                    const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
                    const amount = invoice?.total ? parseFloat(invoice.total) : 0;

                    return (
                      <TableRow key={request.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {attrs.requestNumber || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {provider?.name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {provider?.area || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {provider?.phone || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {attrs.createdAt ? formatDate(attrs.createdAt) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {attrs.assignedAt ? formatDate(attrs.assignedAt) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {attrs.completedDate ? formatDate(attrs.completedDate) : '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="textSecondary">
              Total Requests: {modalRequests.length}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Total Amount: {formatCurrency(
                modalRequests.reduce((sum, request) => {
                  const attrs = request.attributes || request;
                  const invoice = attrs.invoice?.data?.attributes || attrs.invoice;
                  const amount = invoice?.total ? parseFloat(invoice.total) : 0;
                  return sum + amount;
                }, 0)
              )}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleDownloadModalCSV}
          >
            Download CSV
          </Button>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceProviderReport;
